/**
 * Client Habits Page - Daily habit checklist (Coach-driven, Epic 5 MVP+)
 * Client can ONLY toggle habits. Coach manages definitions.
 */
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, CheckSquare, Check,
} from "lucide-react";
import { useDailyHabits, useMyHabits, toggleHabit } from "../hooks/useNutrition";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Toaster, toast } from "sonner";

const containerV = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.06 } } };
const itemV = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } };

export default function HabitsPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split("T")[0];
  const { habits: dailyHabits, mutate: mutateDailyHabits } = useDailyHabits(today);
  const { habits: allHabits } = useMyHabits();

  const completedCount = dailyHabits.filter((h) => h.completed).length;
  const totalCount = dailyHabits.length;

  const handleToggle = async (habitId, currentCompleted) => {
    try {
      await toggleHabit(habitId, today, !currentCompleted);
      mutateDailyHabits();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pb-20">
      <Toaster position="top-right" richColors />
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard" className="text-zinc-400 hover:text-white -ml-2">
            <ArrowLeft className="w-5 h-5 mr-1" /> Retour
          </Button>
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-emerald-400" />
            <span className="font-heading font-bold text-white">MES HABITUDES</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        <motion.div variants={containerV} initial="hidden" animate="show" className="space-y-6">
          {/* Score */}
          {totalCount > 0 && (
            <motion.div variants={itemV}>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Progression du jour</p>
                    <p className="text-2xl font-bold text-white">{completedCount}/{totalCount}</p>
                  </div>
                  <div className="w-16 h-16 relative">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#27272a" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15.5" fill="none"
                        stroke={completedCount === totalCount ? "#34d399" : "#a3e635"}
                        strokeWidth="3" strokeDasharray={`${totalCount > 0 ? (completedCount / totalCount) * 97.4 : 0} 97.4`}
                        strokeLinecap="round" className="transition-all duration-500" />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">
                      {totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0}%
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Daily checklist */}
          <motion.div variants={itemV}>
            <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Checklist du jour</h3>
            {dailyHabits.length > 0 ? (
              <div className="space-y-2">
                {dailyHabits.map((h) => (
                  <Card key={h.habit_id} className={`border transition-all ${h.completed ? "bg-emerald-900/20 border-emerald-800/50" : "bg-zinc-900/50 border-zinc-800"}`}
                    data-testid={`habit-${h.habit_id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <button
                        onClick={() => handleToggle(h.habit_id, h.completed)}
                        data-testid={`toggle-habit-${h.habit_id}`}
                        className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                          h.completed
                            ? "bg-emerald-400 border-emerald-400 text-zinc-950"
                            : "border-zinc-600 hover:border-emerald-400"
                        }`}
                      >
                        {h.completed && <Check className="w-4 h-4" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${h.completed ? "text-emerald-300 line-through" : "text-white"}`}>{h.name}</p>
                        {h.description && <p className="text-xs text-zinc-500 truncate">{h.description}</p>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-10 text-center">
                  <CheckSquare className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500">Aucune habitude définie par votre coach</p>
                  <p className="text-zinc-600 text-sm mt-1">Votre coach peut créer des habitudes depuis votre profil</p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
}
